
import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@/types/auth';
import { clearCachedProfile, setCachedProfile } from '@/utils/profile-cache';

// Create user object utility
const createUserObject = (userId: string, email: string, username?: string): User => ({
  id: userId,
  email,
  username,
});

export const useAuthSubscription = (
  fetchProfile: (userId: string) => Promise<any>,
  dispatch: Function,
  user: User | null
) => {
  const authChangeSubscription = useRef<{ unsubscribe: () => void } | null>(null);
  const isMounted = useRef(false);

  // Utility to clean up subscription
  const cleanupSubscription = useCallback(() => {
    if (authChangeSubscription.current) {
      console.log('Cleaning up auth subscription');
      authChangeSubscription.current.unsubscribe();
      authChangeSubscription.current = null;
    }
  }, []);

  // Handle auth state changes - CRITICAL: Only set up once to avoid duplication
  useEffect(() => {
    isMounted.current = true;
    
    // Skip if already initialized
    if (authChangeSubscription.current) {
      console.log('Auth listener already exists, skipping setup');
      return;
    }
    
    console.log('Setting up auth listener');
    
    try {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
        console.log(`Auth state changed: ${event}`, {
          userId: session?.user?.id,
        });

        if (!isMounted.current) {
          return;
        }

        // Handle both SIGNED_IN and INITIAL_SESSION events when user has a session
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
          console.log(`User has session (${event}):`, {
            userId: session.user.id,
          });
          
          // First initialize with user data
          const user = createUserObject(session.user.id, session.user.email!);
          dispatch({ type: 'SET_USER', payload: user });
          
          // Then fetch profile
          try {
            console.log('Fetching profile after session detected');
            const profile = await fetchProfile(session.user.id);
            
            if (!isMounted.current) return;

            console.log('Profile fetch result:', { hasProfile: !!profile, profileData: profile });

            if (profile) {
              console.log('Profile fetched successfully:', {
                username: profile.username,
                display_name: profile.display_name,
                profile_image_url: profile.profile_image_url,
                has_completed_onboarding: profile.has_completed_onboarding
              });
              
              // Check if user is banned
              if (profile.is_banned) {
                console.warn('User is banned, signing out');
                // Sign out the banned user
                try {
                  await supabase.auth.signOut();
                } catch (signOutError) {
                  console.error('Error signing out banned user:', signOutError);
                }
                
                // Redirect to banned page
                if (typeof window !== 'undefined') {
                  window.location.href = '/banned';
                }
                return;
              }
              
              setCachedProfile(session.user.id, profile);
              console.log('Dispatching SET_PROFILE action with profile data');
              dispatch({ type: 'SET_PROFILE', payload: profile });
              
              // Update user with username
              if (profile.username) {
                console.log('Updating user with username:', profile.username);
                const userWithUsername = createUserObject(
                  session.user.id, 
                  session.user.email!, 
                  profile.username
                );
                dispatch({ type: 'SET_USER', payload: userWithUsername });
              } else {
                console.warn('Profile has no username!');
              }
            } else {
              console.warn('No profile found for user:', session.user.id);
              // Still dispatch SET_PROFILE with null to clear any stale data
              dispatch({ type: 'SET_PROFILE', payload: null });
            }
            
            // Now we can set loading to false and mark as initialized
            console.log('Setting loading=false and initialized=true');
            dispatch({ type: 'SET_LOADING', payload: false });
            dispatch({ type: 'SET_INITIALIZED', payload: true });
          } catch (error) {
            console.error('Error handling session:', error);
            dispatch({ type: 'SET_LOADING', payload: false });
            dispatch({ type: 'SET_INITIALIZED', payload: true });
          }
        } else if (event === 'INITIAL_SESSION' && !session) {
          // No session on initial load - user is not signed in
          console.log('No session found on initial load');
          dispatch({ type: 'SET_LOADING', payload: false });
          dispatch({ type: 'SET_INITIALIZED', payload: true });
        } else if (event === 'SIGNED_OUT') {
          console.log('User signed out');
          if (user?.id) {
            clearCachedProfile(user.id);
          }
          dispatch({ type: 'RESET' });
        }
      });

      authChangeSubscription.current = subscription;
      console.log('Auth listener set up successfully');
    } catch (error) {
      console.error('Error setting up auth listener:', error);
      dispatch({ type: 'SET_LOADING', payload: false });
      dispatch({ type: 'SET_ERROR', payload: error as Error });
    }

    return () => {
      isMounted.current = false;
      cleanupSubscription();
    };
  }, []); // Critical: empty dependency array to ensure this only runs once

  return { cleanupSubscription };
};
